// Port of BivouacEngine/RegulationResolver.swift (RegulatoryAssessment and RegulationResolver).

import type {
  BivouacReason,
  BivouacReasonKind,
  Confidence,
  EpochMs,
  GeoCoordinate,
  GeoDataQuerying,
  JurisdictionLevel,
  ProtectedArea,
  ReasonImpact,
  Regulation,
  RegulatoryState,
  UserReport,
} from '../core/models';
import {
  combineRegulatoryStates,
  effectImpliedState,
  isBlockingState,
  isWildlifeZone,
  jurisdictionSpecificity,
  minConfidence,
  overnightRuleImpliedState,
  protectedAreaContains,
  protectedAreaDistance,
  reportIsExpired,
} from '../core/domain';
import { distance } from '../core/geo';
import { localCalendar, seasonContainsOn, type Calendar } from '../nav/calendar';
import { BivouacLog } from './bivouacLog';

/**
 * Result of resolving the regulatory situation of one location for one night.
 *
 * `state` never means "legal" or "allowed": the best non-official state is `noKnownRestriction` ("no restriction found
 * in the downloaded datasets"), which always comes with the reasons needed to tell the hiker what was *not* checked.
 */
export interface RegulatoryAssessment {
  /** Resolved state (never `official` — only designated facilities are official). */
  state: RegulatoryState;
  /** Confidence in `state` (min of the contributing records; `low` for synthetic input or outside coverage). */
  confidence: Confidence;
  /** Machine-readable explanation, in resolution order. */
  reasons: BivouacReason[];
  /**
   * Regulations applicable at the location **and in force at some point of the assessed night** (the ones that shaped
   * `state`), most general jurisdiction first.
   */
  regulations: Regulation[];
  /**
   * Protected areas containing the location or within `RegulationResolver.proximityBufferMeters` of it (use
   * `protectedAreaContains` to tell them apart).
   */
  protectedAreas: ProtectedArea[];
  /** Whether any regulatory coverage declares the location. */
  isWithinCoverage: boolean;
  /** Jurisdiction levels declared by the covering datasets (sorted from general to specific). */
  coveredLevels: JurisdictionLevel[];
  /** Whether any contributing input (coverage, protected area, regulation) is synthetic. */
  usesSyntheticData: boolean;
}

/** `JurisdictionLevel.allCases` (general to specific). */
const allJurisdictionLevels: readonly JurisdictionLevel[] = [
  'international',
  'federal',
  'cantonal',
  'municipal',
  'protectedAreaAuthority',
  'landowner',
];

const compareStrings = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Resolves the regulatory state of a location for an overnight stay from the offline data.
 *
 * Precedence rules (documented in BIVOUAC_LOGIC.md; every rule can only make the result *more* restrictive or *less*
 * confident, never the opposite):
 *
 * **The night.** `date` stands for the night that starts on its calendar day and ends on the next one. A seasonal
 * window or an effective date that applies on *either* day applies to the night (see `appliesDuringNight`).
 *
 * 1. **Coverage.** No regulatory coverage at the point → contributes `unknown` with reason `outsideRegulatoryCoverage`
 *    and confidence `low`. A *known* restriction or prohibition found at the point still applies.
 * 2. **Protected areas containing the point.** Restrictions active during the night → `overnightRule` implied state
 *    (reason `insideProtectedArea` / `insideWildlifeZone(seasonallyActive: true)`), raised by the access rule. A
 *    seasonal area outside its season → `caution`.
 * 3. **Regulations** applicable at the point and in force during the night → the effect's implied state.
 * 4. **Combination.** Most restrictive wins, with `noKnownRestriction` as the baseline inside coverage. `unknown` is
 *    sticky unless a *blocking* state applies.
 * 5. **Local rules.** `noKnownRestriction` without municipal coverage → reason `noAuthoritativeLocalRule` and
 *    confidence at most `medium` (5b: `noKnownRestriction` is always at most `medium`).
 * 6. **User reports** can only add caution (`regulationSignObserved` within 300 m + fuzzing radius).
 * 7. **Synthetic data.** Any synthetic contributing input → confidence `low` and reason `syntheticData`.
 * 8. **Proximity.** Outside, but within 200 m of, an active blocking protected area → `nearProtectedArea`, `caution`.
 *
 * The resolver never returns `official`; that state is reserved for designated facilities and assigned by the
 * `SleepTonightPlanner`.
 */
export class RegulationResolver {
  /** Rule 8 buffer around restricted/prohibited protected areas (m). */
  static readonly proximityBufferMeters = 200;
  /** Radius (m) within which a `regulationSignObserved` report applies (plus its fuzzing radius). */
  static readonly userReportRadiusMeters = 300;

  /** Assesses `coordinate` for the night of `date`. */
  assess(
    coordinate: GeoCoordinate,
    date: EpochMs,
    data: GeoDataQuerying,
    userReports: readonly UserReport[] = [],
    calendar: Calendar = localCalendar,
  ): RegulatoryAssessment {
    const reasons: BivouacReason[] = [];
    const contributions: RegulatoryState[] = [];
    const confidences: Confidence[] = [];
    let usesSynthetic = false;

    // Rule 1 — coverage.
    const coverage = data.regulatoryCoverageAt(coordinate);
    const isWithinCoverage = coverage.length > 0;
    const coveredLevels = allJurisdictionLevels.filter((level) => coverage.some((c) => c.levels.includes(level)));
    if (coverage.some((c) => c.isSynthetic || c.provenance.isSynthetic)) usesSynthetic = true;
    if (!isWithinCoverage) {
      contributions.push('unknown');
      confidences.push('low');
      reasons.push({ kind: { kind: 'outsideRegulatoryCoverage' }, impact: 'caution', category: 'regulation' });
    }

    // Rules 2 and 8 — protected areas containing or near the point.
    const buffer = RegulationResolver.proximityBufferMeters;
    const listedAreas: ProtectedArea[] = [];
    const nearReasons: BivouacReason[] = [];
    const nearbyAreas = data
      .protectedAreasNear(coordinate, buffer)
      .slice()
      .sort((a, b) => compareStrings(a.name, b.name) || compareStrings(a.id, b.id));
    for (const area of nearbyAreas) {
      const isActive = RegulationResolver.isActiveDuringNight(area, date, calendar);
      const areaSynthetic = area.isSynthetic || area.provenance.isSynthetic;
      if (protectedAreaContains(area, coordinate)) {
        listedAreas.push(area);
        usesSynthetic = usesSynthetic || areaSynthetic;
        confidences.push(area.confidence);
        const state = isActive ? RegulationResolver.activeState(area) : 'caution';
        contributions.push(state);
        reasons.push(RegulationResolver.insideReason(area, isActive, state));
      } else {
        const meters = protectedAreaDistance(area, coordinate);
        if (!(meters <= buffer)) continue;
        listedAreas.push(area);
        // Rule 8 applies only to areas whose restrictions are active and blocking.
        if (!isActive || !isBlockingState(RegulationResolver.activeState(area))) continue;
        usesSynthetic = usesSynthetic || areaSynthetic;
        confidences.push(area.confidence);
        contributions.push('caution');
        nearReasons.push({
          kind: { kind: 'nearProtectedArea', name: area.name, meters },
          impact: 'caution',
          category: 'regulation',
        });
      }
    }

    // Rule 3 — regulations in force at some point of the night.
    const inForce = data
      .regulationsApplicableAt(coordinate)
      .filter((r) => RegulationResolver.isInEffectDuringNight(r, date, calendar))
      .sort(
        (a, b) =>
          jurisdictionSpecificity[a.jurisdiction.level] - jurisdictionSpecificity[b.jurisdiction.level] ||
          compareStrings(a.id, b.id),
      );
    for (const regulation of inForce) {
      const state = effectImpliedState(regulation.effect);
      const isSynthetic = regulation.isSynthetic || regulation.provenance.isSynthetic;
      usesSynthetic = usesSynthetic || isSynthetic;
      contributions.push(state);
      confidences.push(regulation.confidence);
      reasons.push({
        kind: { kind: 'regulation', title: regulation.title, effect: regulation.effect, isSynthetic },
        impact: RegulationResolver.impact(state),
        category: 'regulation',
      });
    }
    reasons.push(...nearReasons);

    // Rule 6 — user reports can only add caution.
    const reportRadius = RegulationResolver.userReportRadiusMeters;
    const signReports = userReports.filter((report) => {
      if (report.kind !== 'regulationSignObserved' || reportIsExpired(report, date)) return false;
      let radius = reportRadius;
      const precision = report.precision;
      if (precision.kind === 'fuzzed' && Number.isFinite(precision.radiusMeters) && precision.radiusMeters > 0) {
        radius += precision.radiusMeters;
      }
      return distance(report.coordinate, coordinate) <= radius;
    });
    if (signReports.length > 0) {
      contributions.push('caution');
      reasons.push({
        kind: { kind: 'userReport', reportKind: 'regulationSignObserved' },
        impact: 'caution',
        category: 'regulation',
      });
    }

    // Rule 4 — most restrictive wins; unknown is sticky unless something blocks.
    const baseline: RegulatoryState = isWithinCoverage ? 'noKnownRestriction' : 'unknown';
    let state = combineRegulatoryStates([...contributions, baseline]);
    if (contributions.includes('unknown') && !isBlockingState(state)) state = 'unknown';
    if (state === 'official') {
      // Unreachable by construction (no rule implies official); guard the honesty contract anyway.
      BivouacLog.logger.fault('RegulationResolver produced .official for terrain; downgraded to unknown');
      state = 'unknown';
    }

    // Rule 5 — no authoritative municipal rule.
    if (state === 'noKnownRestriction' && !coveredLevels.includes('municipal')) {
      confidences.push('medium');
      reasons.push({ kind: { kind: 'noAuthoritativeLocalRule' }, impact: 'caution', category: 'regulation' });
    }
    // Rule 5b — "no known restriction" is an absence of evidence: landowner consent and signage are never covered by
    // any dataset, so its confidence is capped at `medium` even with municipal coverage.
    if (state === 'noKnownRestriction') confidences.push('medium');

    // Rule 7 — synthetic input.
    if (usesSynthetic) {
      confidences.push('low');
      reasons.push({ kind: { kind: 'syntheticData' }, impact: 'caution', category: 'dataQuality' });
    }

    return {
      state,
      confidence: minConfidence(...confidences),
      reasons,
      regulations: inForce,
      protectedAreas: listedAreas,
      isWithinCoverage,
      coveredLevels,
      usesSyntheticData: usesSynthetic,
    };
  }

  // MARK: Helpers

  /**
   * Whether `isActive` holds on the calendar day of `date` or on the following day — i.e. at some point of the night
   * that starts on `date`'s day. Restrictions that begin at midnight therefore apply to the night before, which is the
   * conservative reading (sleeping there would breach them in the early morning).
   */
  static appliesDuringNight(isActive: (date: EpochMs) => boolean, date: EpochMs, calendar: Calendar): boolean {
    if (isActive(date)) return true;
    return isActive(calendar.addingDays(1, date));
  }

  /** `ProtectedArea.isActive(on:calendar:)` for the night of `date`. */
  static isActiveDuringNight(area: ProtectedArea, date: EpochMs, calendar: Calendar): boolean {
    const window = area.seasonalWindow;
    if (!window) return true;
    return RegulationResolver.appliesDuringNight((d) => seasonContainsOn(window, d, calendar), date, calendar);
  }

  /** `Regulation.isInEffect(at:calendar:)` for the night of `date`. */
  static isInEffectDuringNight(regulation: Regulation, date: EpochMs, calendar: Calendar): boolean {
    return RegulationResolver.appliesDuringNight((d) => isInEffect(regulation, d, calendar), date, calendar);
  }

  /**
   * State implied by a protected area whose restrictions are active (rule 2). Public so UI banners use the exact same
   * combination of overnight and access rules as the engine.
   */
  static activeState(area: ProtectedArea): RegulatoryState {
    const states: RegulatoryState[] = [overnightRuleImpliedState(area.overnightRule)];
    switch (area.accessRule) {
      case 'noAccess':
        states.push('prohibited');
        break;
      case 'stayOnMarkedTrails':
        states.push('restricted');
        break;
      case 'none':
      case 'unknown':
        break;
    }
    const combined = combineRegulatoryStates(states);
    // An unknown overnight rule stays unknown unless the access rule is blocking.
    if (states.includes('unknown') && !isBlockingState(combined)) return 'unknown';
    return combined;
  }

  static insideReason(area: ProtectedArea, isActive: boolean, state: RegulatoryState): BivouacReason {
    const kind: BivouacReasonKind = isWildlifeZone(area.kind)
      ? { kind: 'insideWildlifeZone', name: area.name, seasonallyActive: isActive }
      : { kind: 'insideProtectedArea', name: area.name, areaKind: area.kind };
    return { kind, impact: RegulationResolver.impact(state), category: 'regulation' };
  }

  /** Reason impact matching a regulatory state. */
  static impact(state: RegulatoryState): ReasonImpact {
    switch (state) {
      case 'prohibited':
      case 'restricted':
        return 'blocking';
      case 'caution':
      case 'unknown':
        return 'caution';
      case 'noKnownRestriction':
      case 'official':
        return 'neutral';
    }
  }
}

/**
 * `Regulation.isInEffect(at:calendar:)`: effective dates, then the seasonal window in `calendar`. Same rule as
 * `regulationIsInEffect` in src/core/domain.ts, which always uses the device's local time.
 */
function isInEffect(regulation: Regulation, date: EpochMs, calendar: Calendar): boolean {
  const { effectiveFrom, effectiveUntil } = regulation.source;
  if (effectiveFrom !== undefined && date < effectiveFrom) return false;
  if (effectiveUntil !== undefined && date > effectiveUntil) return false;
  if (regulation.seasonalWindow) return seasonContainsOn(regulation.seasonalWindow, date, calendar);
  return true;
}
